import * as React from 'react'
import { useMemo, useState } from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Drawer, TemplatizedTextField, Well } from '@pushly/aqe/lib/components'
import {
    CustomQueryParametersEditor,
    toCustomQueryParameterEntries,
    toCustomQueryParameterRecord,
    validateCustomQueryParameters,
} from '@pushly/aqe/lib/components'
import type { CustomQueryParameterEntry } from '@pushly/aqe/lib/components'
import type { MacroType } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Button as AntButton, Divider, Form, Input, InputNumber, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import CopyToClipboard from 'react-copy-to-clipboard'
import * as deepEqual from 'react-fast-compare'
import * as randomstring from 'randomstring'
import 'jsoneditor-react/es/editor.min.css'

function calculateRawTtlSecondsFromValue(value: number, metric: 'days' | 'hours' | 'minutes' | 'seconds'): number {
    let computed = value

    switch (metric) {
        case 'minutes':
            computed = computed * 60
            break
        case 'hours':
            computed = computed * 60 * 60
            break
        case 'days':
            computed = computed * 60 * 60 * 24
            break
    }

    return computed
}

function calculateValueFromRawTtlSeconds(value: number, metric: 'days' | 'hours' | 'minutes' | 'seconds'): number {
    let computed = value

    switch (metric) {
        case 'minutes':
            computed = computed / 60
            break
        case 'hours':
            computed = computed / 60 / 60
            break
        case 'days':
            computed = computed / 24 / 60 / 60
            break
    }

    return computed
}

interface IAccountNotificationEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

const UTM_MACRO_TYPES: MacroType[] = ['domain', 'location', 'notification', 'custom']

export const AccountNotificationEditor: React.FC<IAccountNotificationEditor> = ({
    account,
    visible,
    onClose,
    onAccountUpdated,
}) => {
    const accountService = useMemo(() => Container.get(AccountService), [])

    const [form] = Form.useForm()
    // Re-render on any field change so disableSubmit reflects the latest values.
    Form.useWatch([], form)

    const [customUtmParams, setCustomUtmParams] = useState<CustomQueryParameterEntry[]>(() =>
        toCustomQueryParameterEntries(account.added_notification_query_params),
    )

    const rawDefaultTtlSeconds = account.default_notification_ttl_seconds
    const defaultTtlMetric = account.display_meta?.default_notification_ttl_metric || 'days'
    const defaultTtlValue = calculateValueFromRawTtlSeconds(rawDefaultTtlSeconds, defaultTtlMetric)

    const computeChangedAccountValues = (): Partial<AccountDto> => {
        const values: Partial<AccountDto> = {}
        const formValues = form.getFieldsValue()

        // Keywords
        let keywords = (formValues.keywords || '').split(/\n/)
        keywords = keywords.map((flag: string) => flag.toLowerCase().trim()).filter((kw: string) => !!kw && kw !== '')
        if (!deepEqual(keywords, account.keywords)) {
            values.keywords = keywords
        }

        // Lifespan
        const ttlValue = formValues.ttlValue
        const ttlMetric = formValues.ttlMetric
        const computedTtlSeconds = calculateRawTtlSecondsFromValue(ttlValue, ttlMetric)
        if (computedTtlSeconds !== account.default_notification_ttl_seconds) {
            values.default_notification_ttl_seconds = computedTtlSeconds
            values.display_meta = {
                default_notification_ttl_metric: ttlMetric,
            }
        }

        // Media
        if (formValues.mediaQuality !== account.media_quality) {
            values.media_quality = formValues.mediaQuality
        }

        // UTM Params
        const utmValues: any = {}
        if (formValues.utmSource) utmValues.utm_source = formValues.utmSource
        if (formValues.utmMedium) utmValues.utm_medium = formValues.utmMedium
        if (formValues.utmCampaign) utmValues.utm_campaign = formValues.utmCampaign
        if (formValues.utmTerm) utmValues.utm_term = formValues.utmTerm
        if (formValues.utmContent) utmValues.utm_content = formValues.utmContent

        // Custom utm extraction
        const addedNotificationQueryParams = toCustomQueryParameterRecord(customUtmParams)

        if (!deepEqual(utmValues, account.utm_params)) {
            values.utm_params = utmValues
        }

        if (!deepEqual(addedNotificationQueryParams, account.added_notification_query_params)) {
            values.added_notification_query_params = addedNotificationQueryParams
        }

        return values
    }

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const mergedValues = computeChangedAccountValues()

            const customQueryParamError = validateCustomQueryParameters(customUtmParams)
            if (customQueryParamError) {
                throw { customUtmParams: { errors: [{ message: customQueryParamError }] } }
            }

            const { ok, data } = await accountService.patch(account.id, mergedValues, true)

            if (ok) {
                onAccountUpdated(data)
                onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }




    const currentValues = computeChangedAccountValues()

    return (
        <Drawer
            className={classnames('adf-notif-settings-editor')}
            title="Update Notification Settings"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                form={form}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Keywords" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        name="keywords"
                        label="Default Keywords"
                        initialValue={(account.keywords || []).join('\n')}
                        extra={
                            <span>
                                <InfoCircleOutlined /> Keywords must be entered one per line.
                            </span>
                        }
                    >
                        <Input.TextArea />
                    </Form.Item>
                </Well>

                <Divider />

                <Well title="Delivery" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        name="ttlValue"
                        label="Default Lifespan"
                        initialValue={defaultTtlValue}
                    >
                        <Input
                            autoComplete="off"
                            addonAfter={
                                <Form.Item name="ttlMetric" initialValue={defaultTtlMetric} noStyle>
                                    <Select>
                                        <Select.Option value="minutes">minute(s)</Select.Option>
                                        <Select.Option value="hours">hour(s)</Select.Option>
                                        <Select.Option value="days">day(s)</Select.Option>
                                    </Select>
                                </Form.Item>
                            }
                        />
                    </Form.Item>
                </Well>

                <Divider />

                <Well title="Media" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        name="mediaQuality"
                        label="Default Quality"
                        initialValue={account.media_quality || 40}
                    >
                        <InputNumber min={30} max={100} />
                    </Form.Item>
                </Well>

                <Divider />

                <Well title="UTM Parameters" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        name="utmSource"
                        label="utm_source"
                        initialValue={account.utm_params?.utm_source}
                        rules={[
                            {
                                required: true,
                                message: 'utm_source is required.',
                            },
                        ]}
                    >
                        <TemplatizedTextField
                            placeholder="utm source"
                            hideEmojis={true}
                            macroTypes={UTM_MACRO_TYPES}
                        />
                    </Form.Item>
                    <Form.Item
                        name="utmMedium"
                        label="utm_medium"
                        initialValue={account.utm_params?.utm_medium}
                    >
                        <TemplatizedTextField
                            placeholder="utm medium"
                            hideEmojis={true}
                            macroTypes={UTM_MACRO_TYPES}
                        />
                    </Form.Item>
                    <Form.Item
                        name="utmCampaign"
                        label="utm_campaign"
                        initialValue={account.utm_params?.utm_campaign}
                    >
                        <TemplatizedTextField
                            placeholder="utm campaign"
                            hideEmojis={true}
                            macroTypes={UTM_MACRO_TYPES}
                        />
                    </Form.Item>
                    <Form.Item
                        name="utmTerm"
                        label="utm_term"
                        initialValue={account.utm_params?.utm_term}
                    >
                        <TemplatizedTextField
                            placeholder="utm term"
                            hideEmojis={true}
                            macroTypes={UTM_MACRO_TYPES}
                        />
                    </Form.Item>
                    <Form.Item
                        name="utmContent"
                        label="utm_content"
                        initialValue={account.utm_params?.utm_content}
                    >
                        <TemplatizedTextField
                            placeholder="utm content"
                            hideEmojis={true}
                            macroTypes={UTM_MACRO_TYPES}
                        />
                    </Form.Item>
                </Well>

                <Divider />

                <CustomQueryParametersEditor
                    value={customUtmParams}
                    onChange={setCustomUtmParams}
                    macroTypes={UTM_MACRO_TYPES}
                    scopeLabel="organization"
                />
            </Form>
        </Drawer>
    )
}

interface IAccountNotificationWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IANWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountNotificationWell extends React.Component<IAccountNotificationWell, IANWState> {
    public state: IANWState = {
        showDrawer: false,
    }

    protected appState: AppState

    public constructor(props: IAccountNotificationWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Notification Settings"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderKeywordsWell()}
                            {this.renderTtlWell()}
                            {this.renderMediaWell()}
                            {this.renderUtmWell()}
                            {this.renderCustomUtmParamsWell()}

                            <AccountNotificationEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderKeywordsWell(): React.ReactNode {
        return (
            <Well.Section
                className={classnames('adf-notif-editor-keywords-display')}
                title="Keywords"
            >
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Keywords</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.props.account.keywords?.join(', ') || 'No keywords'}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>Keywords will be available on all domains associated with this account.</span>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderTtlWell() {
        const rawTtlSeconds = this.account.default_notification_ttl_seconds
        const ttlMetric = this.account.display_meta?.default_notification_ttl_metric || 'days'
        const ttlValue = calculateValueFromRawTtlSeconds(rawTtlSeconds, ttlMetric)

        return (
            <Well.Section title="Delivery">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Lifespan</div>
                    <div className={classnames('form-layout-row-value')}>
                        {!!rawTtlSeconds ? `${ttlValue} ${ttlMetric.replace(/s$/, '(s)')}` : 'No default'}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>System default is 7 days.</span>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderMediaWell() {
        return (
            <Well.Section title="Media">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Image Quality</div>
                    <div className={classnames('form-layout-row-value')}>{this.props.account.media_quality || 40}</div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>
                            Quality can be set anywhere between 30 & 100. Values lower than 40 may significantly impact
                            images.
                        </span>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderUtmWell() {
        const account = this.props.account
        let utmParams: any = {}
        if (account) {
            utmParams = account.utm_params || {}
        }

        return (
            <Well.Section title="UTM Parameters">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_source</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_source && utmParams.utm_source}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_medium</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_medium && utmParams.utm_medium}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_campaign</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_campaign && utmParams.utm_campaign}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_term</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_term && utmParams.utm_term}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_content</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_content && utmParams.utm_content}
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderQueryParams() {
        const queryParams = this.account.added_notification_query_params || {}
        const queryParamsStr = JSON.stringify(queryParams, null, 2)

        return (
            <Well.Section title="Added Notification Query Params">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Params</div>
                    <div className={classnames('form-layout-row-value', 'query-params can-copy')}>
                        <CopyToClipboard onCopy={() => this.handleCopied('query params')} text={queryParamsStr}>
                            <pre>{queryParamsStr}</pre>
                        </CopyToClipboard>
                    </div>
                </div>
            </Well.Section>
        )
    }

    protected renderCustomUtmParamsWell() {
        const queryParams = this.account.added_notification_query_params || {}

        return (
            <Well.Section title="Custom Query Parameters" className="custom-utm-parameter-well">
                {Object.keys(queryParams).length === 0 && <div>No custom query parameters have been added.</div>}
                {Object.entries(queryParams).map(([name, config], idx) => (
                    <div key={idx} className={classnames('form-layout-row')}>
                        <div className={classnames('form-layout-row-label')}>{name}</div>
                        <div className={classnames('form-layout-row-value')}>{config.default}</div>
                    </div>
                ))}
            </Well.Section>
        )
    }

    protected get account(): AccountDto {
        return this.props.account
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
